import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Plus, CheckCircle, XCircle, Warning, Eye, ArrowsClockwise } from '@phosphor-icons/react';
import api from '../lib/api';

const STATUS_LABEL = {
  open: 'Open',
  forwarded: 'Forwarded',
  assigned: 'Assigned',
  in_progress: 'In Progress',
  auto_refund_pending: 'Refund Pending',
  refund_approved: 'Refund Approved',
  refund_rejected: 'Refund Rejected',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATUS_COLOR = {
  open: 'bg-gray-100 text-gray-700 border-gray-300',
  forwarded: 'bg-blue-100 text-blue-700 border-blue-300',
  assigned: 'bg-amber-100 text-amber-800 border-amber-300',
  in_progress: 'bg-amber-100 text-amber-800 border-amber-300',
  auto_refund_pending: 'bg-red-100 text-red-700 border-red-400 font-bold',
  refund_approved: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  refund_rejected: 'bg-gray-200 text-gray-700 border-gray-400',
  resolved: 'bg-green-100 text-green-700 border-green-300',
  closed: 'bg-gray-100 text-gray-600 border-gray-300',
};

const SLA_BADGE = {
  normal: 'bg-gray-50 text-gray-600 border-gray-300',
  warning: 'bg-amber-50 text-amber-700 border-amber-300',
  urgent: 'bg-orange-50 text-orange-700 border-orange-400',
  breach: 'bg-red-50 text-red-700 border-red-400 animate-pulse',
  ok: 'bg-emerald-50 text-emerald-700 border-emerald-300',
};

const fmtDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';
const fmtMoney = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

export default function ComplaintsPage() {
  const { user } = useAuth();
  const role = user?.role;
  const isDistributor = role === 'distributor';
  const isAdminLike = ['admin', 'owner', 'assistant'].includes(role);
  const isStaff = role === 'staff';
  const isSubStaff = role === 'sub_staff';

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ open: 0, resolved: 0, refund_pending: 0, sla_breach: 0 });
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [showResolve, setShowResolve] = useState(null); // complaint id
  const [showAssign, setShowAssign] = useState(null);
  const [subStaff, setSubStaff] = useState([]);
  const [form, setForm] = useState({ consumer_id: '', issue_description: '', priority: 'medium', refund_amount: '' });
  const [actionTaken, setActionTaken] = useState('');
  const [assignTarget, setAssignTarget] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, st] = await Promise.all([
        api.get('/complaints'),
        api.get('/complaints/stats'),
      ]);
      setItems(list.data || []);
      setStats(st.data || {});
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // For staff role: load own sub-staff for assignment dropdown
  useEffect(() => {
    if (!isStaff) return;
    api.get('/staff/my-substaff').then(r => setSubStaff(r.data || [])).catch(() => setSubStaff([]));
  }, [isStaff]);

  const submitNew = async () => {
    if (!form.consumer_id.trim() || !form.issue_description.trim()) {
      toast.error('Consumer ID aur issue zaroori hain');
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post('/complaints', {
        consumer_id: form.consumer_id.trim(),
        issue_description: form.issue_description.trim(),
        priority: form.priority,
        refund_amount: Number(form.refund_amount || 0),
      });
      toast.success(`Complaint ${data.complaint_number} ${data.auto_assigned ? 'auto-assigned!' : 'created (admin queue)'}`);
      setShowNew(false);
      setForm({ consumer_id: '', issue_description: '', priority: 'medium', refund_amount: '' });
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Create failed');
    } finally { setBusy(false); }
  };

  const submitResolve = async () => {
    if (!actionTaken.trim()) { toast.error('Action taken note zaroori hai'); return; }
    setBusy(true);
    try {
      await api.post(`/complaints/${showResolve}/resolve`, { action_taken: actionTaken.trim(), final_status: 'resolved' });
      toast.success('Complaint resolved');
      setShowResolve(null); setActionTaken('');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Resolve failed'); }
    finally { setBusy(false); }
  };

  const submitAssign = async () => {
    if (!assignTarget) { toast.error('Sub-staff select karo'); return; }
    setBusy(true);
    try {
      await api.post(`/complaints/${showAssign}/assign`, { sub_staff_id: assignTarget });
      toast.success('Assigned');
      setShowAssign(null); setAssignTarget('');
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Assign failed'); }
    finally { setBusy(false); }
  };

  const decideRefund = async (cid, decision) => {
    if (!window.confirm(`${decision === 'approve' ? 'APPROVE' : 'REJECT'} refund? Confirm karo.`)) return;
    try {
      await api.post(`/complaints/${cid}/${decision}-refund`);
      toast.success(`Refund ${decision}d`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-4" data-testid="complaints-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold">Complaints</h1>
          <p className="text-sm text-gray-500">
            {isDistributor && 'Apni complaints raise karo aur status track karo. 4 din me resolve nahi hua to refund admin ke pas approval ke liye chala jayega.'}
            {isSubStaff && 'Aapko assigned complaints. 4 din ke andar resolve karo, warna SLA breach + auto-refund pending ho jayega.'}
            {isStaff && 'Aapki team ko forwarded complaints. Sub-staff ko assign karo ya khud resolve karo.'}
            {isAdminLike && 'Saari complaints — Day-4 SLA breach par refund pending_approval me automatically ban jaata hai. Approve/Reject karo.'}
          </p>
        </div>
        {(isDistributor || isAdminLike) && (
          <Button onClick={() => setShowNew(true)} className="bg-blue-600 hover:bg-blue-700 gap-1" data-testid="new-complaint-btn">
            <Plus size={16} /> New Complaint
          </Button>
        )}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { l: 'Open', v: stats.open, c: 'amber' },
          { l: 'Resolved', v: stats.resolved, c: 'emerald' },
          { l: 'Refund Pending', v: stats.refund_pending, c: 'red' },
          { l: 'SLA Breach', v: stats.sla_breach, c: 'rose' },
        ].map(s => (
          <div key={s.l} className={`bg-${s.c}-50 border border-${s.c}-200 rounded p-3`}>
            <div className={`text-[10px] uppercase text-${s.c}-700 font-bold`}>{s.l}</div>
            <div className="text-2xl font-bold">{s.v ?? 0}</div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto bg-white border rounded">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Consumer ID</th>
              <th className="px-3 py-2 text-left">Issue</th>
              <th className="px-3 py-2 text-left">Priority</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">SLA</th>
              <th className="px-3 py-2 text-left">Created</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} className="text-center text-gray-400 py-8">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={9} className="text-center text-gray-400 py-8">Koi complaint nahi.</td></tr>
            ) : items.map(c => {
              const isMineToResolve = (
                (isSubStaff) || (isStaff) || isAdminLike
              ) && !['resolved', 'closed', 'refund_approved', 'refund_rejected'].includes(c.status);
              return (
                <tr key={c.id} className="border-t hover:bg-gray-50" data-testid={`complaint-${c.id}`}>
                  <td className="px-3 py-2 font-mono text-xs">{c.complaint_number}</td>
                  <td className="px-3 py-2 font-mono text-xs">{c.consumer_id}</td>
                  <td className="px-3 py-2 text-xs max-w-[280px] truncate" title={c.issue_description}>{c.issue_description}</td>
                  <td className="px-3 py-2 text-xs uppercase">{c.priority}</td>
                  <td className="px-3 py-2 text-right">{fmtMoney(c.refund_amount)}</td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border font-medium ${STATUS_COLOR[c.status] || ''}`}>{STATUS_LABEL[c.status] || c.status}</span>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`text-[10px] px-2 py-0.5 rounded border ${SLA_BADGE[c.sla_state] || ''}`}>
                      Day {c.days_elapsed}{c.sla_state === 'breach' ? ' • BREACH' : c.sla_state === 'urgent' ? ' • URGENT' : c.sla_state === 'warning' ? ' • WARN' : ''}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-[11px] text-gray-500">{fmtDate(c.created_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {c.status === 'auto_refund_pending' && isAdminLike && (
                        <>
                          <Button size="sm" className="h-7 text-xs gap-1 bg-emerald-600 hover:bg-emerald-700" onClick={() => decideRefund(c.id, 'approve')} data-testid={`approve-${c.id}`}>
                            <CheckCircle size={12} /> Approve
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-red-300 text-red-600 hover:bg-red-50" onClick={() => decideRefund(c.id, 'reject')} data-testid={`reject-${c.id}`}>
                            <XCircle size={12} /> Reject
                          </Button>
                        </>
                      )}
                      {isMineToResolve && c.status !== 'auto_refund_pending' && (
                        <Button size="sm" className="h-7 text-xs gap-1 bg-green-600 hover:bg-green-700" onClick={() => { setShowResolve(c.id); setActionTaken(''); }} data-testid={`resolve-${c.id}`}>
                          <CheckCircle size={12} /> Resolve
                        </Button>
                      )}
                      {isStaff && !c.assigned_to_substaff_id && c.status !== 'resolved' && (
                        <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={() => { setShowAssign(c.id); setAssignTarget(''); }} data-testid={`assign-${c.id}`}>
                          <ArrowsClockwise size={12} /> Assign
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex"><Button onClick={load} variant="outline" className="gap-1"><Eye size={14} /> Refresh</Button></div>

      {/* New Complaint Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Complaint</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Consumer ID *</label>
              <Input value={form.consumer_id} onChange={e => setForm({ ...form, consumer_id: e.target.value })} placeholder="17-digit consumer ID" className="mt-1 font-mono" data-testid="new-consumer-id" />
            </div>
            <div>
              <label className="text-xs font-semibold">Issue Description *</label>
              <textarea value={form.issue_description} onChange={e => setForm({ ...form, issue_description: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded text-sm" rows={3} placeholder="Detail kya issue hua…" data-testid="new-issue" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold">Priority</label>
                <select value={form.priority} onChange={e => setForm({ ...form, priority: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded text-sm">
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold">Refund Amount (optional)</label>
                <Input type="number" value={form.refund_amount} onChange={e => setForm({ ...form, refund_amount: e.target.value })} placeholder="₹" className="mt-1" />
              </div>
            </div>
            <div className="text-[11px] text-gray-500 bg-blue-50 p-2 rounded border border-blue-200">
              <Warning size={12} className="inline mr-1" />
              System auto-route karega: agar Consumer ID ka booking record mile to us sub-staff ko + parent staff ko forward ho jayega.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submitNew} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-new-complaint">
              {busy ? 'Creating…' : 'Create Complaint'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={!!showResolve} onOpenChange={(v) => !v && setShowResolve(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Resolve Complaint</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="text-xs font-semibold">Action Taken *</label>
            <textarea value={actionTaken} onChange={e => setActionTaken(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" rows={4} placeholder="Detail likho — kya kiya issue ko solve karne ke liye…" data-testid="action-taken-input" />
            <div className="text-[11px] text-emerald-700 bg-emerald-50 p-2 rounded">
              ✓ Resolve karne ke baad complaint band ho jayegi. <b>No refund needed</b> — issue solve ho gaya.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowResolve(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitResolve} disabled={busy} className="bg-green-600 hover:bg-green-700" data-testid="confirm-resolve">
              {busy ? 'Resolving…' : 'Mark Resolved'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Dialog */}
      <Dialog open={!!showAssign} onOpenChange={(v) => !v && setShowAssign(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign to Sub-Staff</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="text-xs font-semibold">Sub-Staff *</label>
            <select value={assignTarget} onChange={e => setAssignTarget(e.target.value)} className="w-full px-3 py-2 border rounded text-sm" data-testid="assign-substaff-select">
              <option value="">— Select —</option>
              {subStaff.map(s => <option key={s.id} value={s.id}>{s.name} · {s.email}</option>)}
            </select>
            {subStaff.length === 0 && (
              <div className="text-[11px] text-amber-700">Aapke under koi sub-staff nahi hai.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssign(null)} disabled={busy}>Cancel</Button>
            <Button onClick={submitAssign} disabled={busy || !assignTarget}>{busy ? 'Assigning…' : 'Assign'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
